import React from "react";

/**
 * Top-level error boundary.
 * Prevents a single buggy component from nuking the whole app; shows a branded
 * recovery screen with a reload button instead.
 *
 * In dev, the error + stack are rendered inline for visibility. In prod,
 * only a friendly message is shown (stack is still posted to console.error).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to whatever observability is wired (PostHog is in index.html)
    console.error("[ErrorBoundary]", error, info?.componentStack);
    if (typeof window !== "undefined" && window.posthog?.capture) {
      try {
        window.posthog.capture("frontend_error_boundary", {
          message: error?.message,
          stack: (error?.stack || "").slice(0, 2000),
          component_stack: (info?.componentStack || "").slice(0, 2000),
        });
      } catch (capErr) {
        // Telemetry SDK threw — never let it cascade past the boundary.
        // eslint-disable-next-line no-console
        console.debug("[ErrorBoundary] posthog capture failed", capErr);
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const isDev = process.env.NODE_ENV !== "production";
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen bp-fade-in grid place-items-center px-4"
        style={{
          background:
            "radial-gradient(900px circle at 20% 20%, hsla(252,90%,80%,0.35), transparent 55%)," +
            "radial-gradient(800px circle at 80% 80%, hsla(330,90%,80%,0.3), transparent 55%)," +
            "linear-gradient(180deg, hsl(260 55% 99%), hsl(330 45% 99%))",
        }}
      >
        <div className="bp-card max-w-md w-full p-6 sm:p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mb-4 text-2xl">
            🫧
          </div>
          <h1 className="font-display font-semibold text-2xl">Something spilled.</h1>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            A part of the page had a hiccup. Reloading usually fixes it — we already got the error on our side.
          </p>
          {isDev && (
            <pre
              data-testid="error-boundary-dev-details"
              className="mt-4 p-3 rounded-lg bg-muted text-left text-[11px] font-mono overflow-x-auto max-h-48"
            >
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          )}
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bp-btn-primary px-5 py-2.5"
              data-testid="error-boundary-reload"
            >
              Reload
            </button>
            <a
              href="/"
              className="inline-flex items-center rounded-xl border border-border bg-white hover:bg-muted px-4 py-2.5 text-sm font-medium"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
